/* eslint-disable react/no-children-prop */
import React, { useEffect, forwardRef, useCallback } from 'react';
import { Checkbox as V3Checkbox } from 'lfs-ui/v3/Checkbox';
import { Select as V3Select } from 'lfs-ui/v3/Select';
import { useFormContext } from 'react-hook-form';
import { Icon } from 'basis';
import {
  InputGroup,
  FormControl,
  FormHelperText,
  FormLabel,
  Input as ChInput,
  Box,
  Text,
  InputLeftElement,
} from '@chakra-ui/react';
import _ from 'lodash';
import styled from '@emotion/styled';

export function FieldMoneyInput({ maxLength = 10, rules, ...props }) {
  return (
    <FieldInput
      type="number"
      leftElement="$"
      maxLength={maxLength}
      {...props}
      rules={{
        required: 'Please enter a whole dollar amount, without cents.',
        min: { value: 1, message: 'Must not be less than $1.' },
        max: { value: 99999999, message: 'Must not be more than $99,999,999.' },
        pattern: {
          value: /^\d+$/,
          message: 'Please enter a whole dollar amount, without cents.',
        },
        ...rules,
      }}
    />
  );
}

/**
 * Build from scratch because as 2024-02 LFS-UI v3 Input component doesn't support left element like $ sign.
 */
export const FieldInput = forwardRef(
  (
    { id, label, conditional, helpText, leftElement, children, rules, maxLength, type = 'text', ...otherProps },
    ref,
  ) => {
    const {
      register,
      formState: { errors },
      unregister,
    } = useFormContext();

    const error = getError(errors, id)?.message;

    const { isConditionMet } = useConditional({
      conditional,
      unregister,
      id,
    });

    const transformValue = useCallback(
      inputValue => {
        if (maxLength && inputValue !== '' && inputValue.length > maxLength) {
          return inputValue.slice(0, maxLength);
        }
        return inputValue;
      },
      [maxLength],
    );

    const onAfterChange = useCallback(
      event => {
        // eslint-disable-next-line no-param-reassign
        event.target.value = transformValue(event.target.value);
      },
      [transformValue],
    );

    if (!isConditionMet) {
      return null;
    }

    const registered = register(id, {
      ...rules,
      onChange: onAfterChange,
      setValueAs: transformValue,
    });

    return (
      <FieldInputWrapper error={!!error}>
        <FormControl id={id}>
          <FormLabel fontWeight={600} htmlFor={id}>
            {label}
          </FormLabel>
          <ErrorBox error={error}>
            <InputGroup>
              {leftElement && (
                <InputLeftElement pointerEvents="none">
                  <b>{leftElement}</b>
                </InputLeftElement>
              )}
              <ChInput
                ref={ref}
                fontWeight="500"
                backgroundColor="white"
                borderRadius="4px"
                border="1px"
                borderColor="#A4A7AE"
                isInvalid={!!error}
                variant="filled"
                data-testid={id}
                error={error}
                maxLength={maxLength}
                type={type}
                {...(type === 'number' && { onKeyPress: numberInputOnKeyPress })}
                {...registered}
                {...otherProps}
              >
                {children}
              </ChInput>
            </InputGroup>
          </ErrorBox>
          {!!helpText && <FormHelperText fontWeight={500}>{helpText}</FormHelperText>}
        </FormControl>
      </FieldInputWrapper>
    );
  },
);

export const HiddenInput = forwardRef(({ id, value, conditional, rules, ...otherProps }, ref) => {
  const { register, unregister } = useFormContext();

  const { isConditionMet } = useConditional({
    conditional,
    unregister,
    id,
  });

  if (!isConditionMet) {
    return null;
  }

  return (
    <input
      ref={ref}
      data-testid={id}
      type="hidden"
      {...register(id, {
        value,
        ...rules,
      })}
      {...otherProps}
    />
  );
});

export function FieldCheckbox({ id, label, conditional, rules, ...otherProps }) {
  const {
    register,
    unregister,
    formState: { errors },
  } = useFormContext();

  const error = getError(errors, id)?.message;

  const { isConditionMet } = useConditional({
    conditional,
    unregister,
    id,
  });

  if (!isConditionMet) {
    return null;
  }

  return (
    <V3CheckboxWrapper error={!!error}>
      <ErrorBox error={error}>
        <V3Checkbox id={id} {...register(id, rules)} {...otherProps}>
          {label}
        </V3Checkbox>
      </ErrorBox>
    </V3CheckboxWrapper>
  );
}

export function FieldSelect({ id, label, options, conditional, rules, ...otherProps }) {
  const {
    register,
    unregister,
    formState: { errors },
  } = useFormContext();

  const error = getError(errors, id)?.message;

  const { isConditionMet } = useConditional({
    conditional,
    unregister,
    id,
  });

  if (!isConditionMet) {
    return null;
  }

  return (
    <V3SelectWrapper error={!!error}>
      <ErrorBox error={error}>
        <V3Select data-testid={id} placeholder="Please select" {...register(id, rules)} label={label} {...otherProps}>
          {options.map((opt, index) => (
            <option value={opt.value} key={index}>
              {opt.label}
            </option>
          ))}
        </V3Select>
      </ErrorBox>
    </V3SelectWrapper>
  );
}

const FieldInputWrapper = styled.div`
  p {
    font-weight: 500;
  }

  input:hover {
    background-color: white;
  }

  ${({ error }) =>
    !error
      ? `
    input:focus {
      border-color: #0156f5;
    }
  `
      : `input:focus {
      border-color: #d83d32;
    }

  `}
`;

const V3SelectWrapper = styled.div`
  label {
    font-weight: 600;
  }
  select {
    background-color: white;
    border: 1px solid #a4a7ae;
    border-radius: 4px;
    font-weight: 500;
    height: 48px;
    padding-left: 16px;
  }
  select:has(option[value='']:checked) {
    color: #595959;
  }
  select:hover {
    background-color: white;
  }
  select:focus {
    outline: 0;
    box-shadow: none;
    border-color: ${({ error }) => (error ? '#d83d32' : '#0156f5')};
  }
  ${({ error }) =>
    error
      ? `
    select {
      border-color: #d83d32;
    }
  `
      : ''}
`;

const V3CheckboxWrapper = styled.div`
  label > div > div {
    border: 1px solid ${({ error }) => (error ? '#d83d32' : '#717680')};
    border-radius: 4px;
    background: #ffffff;
  }

  div > div:has(p) {
    border: none;
    background: #ffffff;
    & p {
      font-weight: 500;
    }
  }
  label {
    font-weight: 500;
  }

  && [aria-checked='true'] svg,
  && [data-checked] svg {
    color: #0b2374;
    background: #ffffff;
  }
`;

const ErrorBox = ({ error, children }) => (
  <Box width="100%">
    {children}
    {!!error && (
      <Box css={{ display: 'flex', alignItems: 'flex-start', gap: '4px', paddingTop: '8px' }}>
        <Icon size="20px" name="warning-circle" color="conditional.negative.graphics" />
        <Text fontSize="14px" fontWeight={500} color="#D83D32">
          {error}
        </Text>
      </Box>
    )}
  </Box>
);

function getError(errors, id) {
  const regex = /\.(\d+)(?=\.|$)/g;
  const position = id.replace(regex, '[$1]');
  return _.get(errors, position);
}

function useConditional({ conditional = true, unregister, id }) {
  const isConditionalFunction = typeof conditional === 'function';
  const isConditionMet = isConditionalFunction ? conditional() : conditional === true;

  useEffect(() => {
    if (!isConditionMet) {
      unregister(id);
    }
  }, [isConditionMet, id, unregister]);

  return { isConditionMet };
}

// Prevent non-numeric characters on number input
// This is needed for browsers that don't support the input type="number" attribute such as Firefox
function numberInputOnKeyPress(event) {
  if (!/^\d+$/.test(event.key)) {
    event.preventDefault();
  }
}
